'use client';

import clsx from 'clsx';
import { FC } from 'react';
import { shallow } from 'zustand/shallow';

import { getSearchDestination } from '@/general/helpers/searchDestination.helper';
import { useRouter } from '@/general/helpers/useRouter';
import { RecentFilterSearch } from '@/hygiene/components/flexible-content/RecentSearch/RecentSearch.type';
import { getRecentSmartSearchId } from '@/stores/recentFilterSearch.helper';
import { recentFilterSearchStore } from '@/stores/recentFilterSearchStore';
import { isEmpty } from '@/utils/isEmpty';
import { pushToDataLayer } from '@/utils/pushToDataLayer';

import { Button } from '../Button/Button';
import { KEYWORD_SEARCH_PROMPTS, KEYWORD_SEARCH_PROMPTS_TITLE } from './KeywordSearch.constants';
import { KeywordSearchResponseSection, KeywordSearchVariant } from './KeywordSearch.types';
import { KeywordSearchResultsSection } from './KeywordSearchResultsSection';
import { useKeywordSearchSections } from './useKeywordSearchSections';

type Props = {
  searchTerm: string;
  variant: KeywordSearchVariant;
  onResultClick?: () => void;
  isSemanticSearchEnabled?: boolean;
};

const PROMPTS_SHOWN = 3;

const getPromptsSection = (recentSearches: RecentFilterSearch[]): KeywordSearchResponseSection => {
  const searched = new Set(recentSearches.map((search) => search.id));

  return {
    title: KEYWORD_SEARCH_PROMPTS_TITLE,
    type: 'prompts',
    items: KEYWORD_SEARCH_PROMPTS.filter((label) => !searched.has(getRecentSmartSearchId(label)))
      .slice(0, PROMPTS_SHOWN)
      .map((label) => ({ label })),
  };
};

export const KeywordSearchResults: FC<Props> = ({
  searchTerm,
  variant,
  onResultClick,
  isSemanticSearchEnabled = false,
}) => {
  const router = useRouter();
  const isEmptySearch = isEmpty(searchTerm);
  const { recentFilterSearches } = recentFilterSearchStore(
    (state) => ({ recentFilterSearches: state.recentFilterSearches }),
    shallow,
  );
  const { sections, isLoading } = useKeywordSearchSections(searchTerm, variant);

  const onClickViewAll = () => {
    onResultClick?.();
    pushToDataLayer({
      event: `search_click_view_all_results`,
    });
    router.push(getSearchDestination(searchTerm));
  };

  const isInline = variant === 'inline'; // PLP page

  return (
    <div
      data-testid="keyword-search-results"
      className={clsx(
        isInline
          ? 'w-full overflow-x-hidden overflow-y-auto'
          : 'pointer-events-none absolute left-0 w-full overflow-x-hidden overflow-y-auto border-t border-bor-neu-bas md:border-t-0',
        {
          'top-16 max-h-[calc(100vh-64px)] min-h-10 md:top-15': variant === 'default',
          'top-18 bottom-0 rounded-sm shadow-elevation-24dp md:top-16': variant === 'popover',
        },
      )}
    >
      <div
        className={clsx('pointer-events-auto mx-auto flex w-full flex-col gap-4 bg-bac-tie1 p-4', {
          'md:max-w-xl md:rounded-sm md:border md:border-bor-neu-bas md:shadow-elevation-24dp mdp:max-w-2xl':
            variant === 'default',
          'rounded-sm': variant === 'popover',
        })}
      >
        {isEmptySearch && recentFilterSearches.length > 0 && (
          <KeywordSearchResultsSection
            section={{ title: 'Recent searches', type: 'recentSearches', items: recentFilterSearches }}
            onResultClick={onResultClick}
          />
        )}

        {isEmptySearch && isSemanticSearchEnabled && (
          <KeywordSearchResultsSection
            section={getPromptsSection(recentFilterSearches)}
            onResultClick={onResultClick}
          />
        )}
        {sections?.map((result) => (
          <KeywordSearchResultsSection key={result.title} section={result} onResultClick={onResultClick} />
        ))}

        {/*
          Header search only: on PLP the bar has its own `Search` submit that routes to the same
          destination, so a `View all` here was a second button for the one action.
        */}
        {!isInline && (
          <div>
            <Button
              variant="primary"
              size="small"
              fullWidth
              onClick={onClickViewAll}
              isLoading={isLoading}
              isDisabledWhileLoading={false}
              data-testid="keyword-search-results-viewAll"
              data-type="navigation"
            >
              {isEmptySearch ? 'View all cars' : `View all results for '${searchTerm}'`}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
